<script setup lang="ts">
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {computed} from "vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import AttachmentLinkUtil from "@/utils/UIConstants/AttachmentLinkUtil.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";
import DTOApprovalCaseStepCandidate = namaTypes.DTOApprovalCaseStepCandidate;

const props = defineProps<{ presenter: EditPresenter }>();
const {darkMode} = storeToRefs(useNamaFlagsStore());

const nextCandidates = computed<DTOApprovalCaseStepCandidate[]>(() => {
  return NamaCommon.getFieldValue(props.presenter.fetchRecord(), "nextCandidates");
})

const getCandidateName = (candidate: DTOApprovalCaseStepCandidate): string => {
  if (!candidate.candidate)
    return '';
  return NamaCommon.nameOfRef(candidate.candidate);
}

const getCandidateLink = (candidate: DTOApprovalCaseStepCandidate): string => {
  if (!candidate.candidate)
    return '';
  return PlaceUtils.linkToObject(candidate.candidate);
}

const openCandidateEntity = (candidate: DTOApprovalCaseStepCandidate): void => {
  const link = getCandidateLink(candidate);
  if (link)
    window.open(link, '_blank');
}

const hasImage = (candidate: DTOApprovalCaseStepCandidate): boolean => {
  return Boolean(candidate.candidate?.hasImage);
}

const getImageUrl = (candidate: DTOApprovalCaseStepCandidate): string => {
  if (!candidate.candidate)
    return '';
  return AttachmentLinkUtil.downloadURLByRecordId(candidate.candidate, DownloadPurpose.THUMBNAIL, false);
}

const formatRequestedOn = (candidate: DTOApprovalCaseStepCandidate): string => {
  return DateFieldUtils.dateAndTimeToString(candidate.requestedOn.toString());
}

</script>

<template>
  <div class="tw-h-full tw-w-full">
    <q-card class="tw-h-full tw-w-full tw-flex tw-flex-col tw-shadow-none tw-bg-transparent tw-p-0">
      <div class="tw-grow">
        <q-scroll-area :dark="darkMode" class="tw-h-full tw-w-full dir-ltr">
          <div class="tw-p-6 tw-flex tw-flex-wrap tw-gap-4">
            <div
              v-for="(candidate, index) in nextCandidates"
              :key="index"
              class="badge-card"
              :class="darkMode ? 'badge-card-dark' : 'badge-card-light'">
              <div class="badge-header">
                <q-avatar class="badge-avatar" size="36px">
                  <img
                    alt="user"
                    v-if="hasImage(candidate)"
                    :src="getImageUrl(candidate)"
                    class="tw-w-full tw-h-full tw-object-cover"
                  />
                  <q-icon v-else name="person" size="18px"/>
                </q-avatar>
                <div class="badge-content">
                  <div class="badge-label">
                    <a
                      :href="getCandidateLink(candidate)"
                      @click.prevent="openCandidateEntity(candidate)"
                      class="candidate-link">
                      {{ getCandidateName(candidate) }}
                    </a>
                  </div>
                  <div class="badge-date">
                    <q-icon name="schedule" size="11px" class="tw-mr-1"/>
                    {{ formatRequestedOn(candidate) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </q-scroll-area>
      </div>
    </q-card>
  </div>
</template>

<style scoped>
.badge-card {
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
}

.badge-card-light {
  background: white;
  border-color: #e0e0e0;
  color: #333;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.badge-card-dark {
  background: #2d2d2d;
  border-color: #404040;
  color: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

.badge-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.badge-avatar {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.badge-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.badge-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: 0.2px;
}

.badge-card-light .badge-label {
  color: #333;
}

.badge-card-dark .badge-label {
  color: white;
}

.badge-date {
  font-size: 11px;
  opacity: 0.75;
  display: flex;
  align-items: center;
}

.badge-card-light .badge-date {
  color: #666;
}

.badge-card-dark .badge-date {
  color: rgba(255, 255, 255, 0.8);
}

.candidate-link {
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s;
}

.candidate-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.badge-card-light .candidate-link {
  color: #1976d2;
}

.badge-card-dark .candidate-link {
  color: #64b5f6;
}
</style>
